/**
 * Client-safe RPC wrappers — fetch from API routes instead of direct DB calls.
 * Import this in "use client" components instead of rpc.ts.
 *
 * Re-exports all types from rpc-drizzle.ts (type-only, no runtime).
 */

export type {
  CurrentUserProfile,
  FeatureVisibility,
  SeedBalanceOverview,
  SeedTransactionRow,
  SeedTransactionResponse,
  DashboardSnapshot,
  DashboardProject,
  SafeConnection,
  SafeConnectionGroup,
  ProjectSnapshotMetadataOptions,
  ProjectSnapshotPlatformConfig,
  ProjectSnapshot,
  ProjectSnapshotV2,
} from "./rpc-drizzle";

import {
  reportWarning,
  suppressErrorReporting,
} from "@/lib/observability/report-error";
import { getAppUrl } from "@/lib/platforms/oauth/types";
import type { CurrentUserProfile } from "./rpc-drizzle";
import type { FeatureVisibility } from "./rpc-drizzle";
import type { SeedBalanceOverview } from "./rpc-drizzle";
import type { DashboardSnapshot } from "./rpc-drizzle";
import type { DashboardProject } from "./rpc-drizzle";
import type { SafeConnection } from "./rpc-drizzle";
import type { SafeConnectionGroup } from "./rpc-drizzle";
import type { ProjectSnapshot, ProjectSnapshotV2 } from "./rpc-drizzle";

const CREDS: RequestInit = { credentials: "include" };

/** Node `fetch` requires an absolute URL; browsers accept root-relative paths. */
function resolveRpcFetchUrl(url: string): string {
  if (typeof window !== "undefined") {
    return url;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  const base = getAppUrl().replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
}

/**
 * Thrown when a fetch call fails due to a network error (no response received).
 * This is distinct from HTTP errors (4xx/5xx) where a response WAS received.
 * React Query catches this and enters proper error state, enabling error UI.
 */
export class RpcNetworkError extends Error {
  public readonly url: string;
  constructor(url: string, cause?: unknown) {
    super(`Network error fetching ${url}`);
    this.name = "RpcNetworkError";
    this.url = url;
    this.cause = cause;
  }
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const resolved = resolveRpcFetchUrl(url);
    const init: RequestInit = { ...CREDS };
    if (typeof window === "undefined") {
      try {
        const { headers } = await import("next/headers");
        const h = await headers();
        const cookie = h.get("cookie");
        if (cookie) {
          const merged = new Headers();
          merged.set("cookie", cookie);
          init.headers = merged;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (typeof msg === "string" && msg.includes("Dynamic server usage")) {
          suppressErrorReporting(
            "rpc_client_cookie_forward_skipped_no_request_context",
            {
              source: "rpc-client",
              feature: "auth",
              action: "forward-cookie-skip",
              extra: { url: url.slice(0, 160) },
            }
          );
        } else {
          reportWarning(err, {
            source: "rpc-client",
            feature: "auth",
            action: "forward-cookie-failed",
            extra: { url: url.slice(0, 160) },
          });
        }
      }
    }

    const res = await fetch(resolved, init);
    if (!res.ok) {
      console.warn(
        `[rpc-client] ${url} returned ${res.status} ${res.statusText}`
      );
      return null;
    }
    return res.json();
  } catch (err) {
    // Network failures (TypeError: Failed to fetch) — throw so React Query
    // enters error state and consumers can show error UI to the user.
    // Without this, queries "succeed" with null/[] and users see empty data
    // with no indication that a network error occurred.
    console.warn(`[rpc-client] ${url} fetch failed:`, err);
    reportWarning(err, { source: "rpc-client", extra: { url } });
    throw new RpcNetworkError(url, err);
  }
}

export async function getCurrentUserProfile(): Promise<CurrentUserProfile | null> {
  return fetchJson<CurrentUserProfile>("/api/auth/me/");
}

export async function getEnabledFeatureFlags(): Promise<
  Record<string, boolean>
> {
  return (
    (await fetchJson<Record<string, boolean>>("/api/feature-flags/")) ?? {}
  );
}

export async function getFeatureVisibility(
  key: string
): Promise<FeatureVisibility> {
  const data = await fetchJson<FeatureVisibility>(
    `/api/feature-flags/?key=${encodeURIComponent(key)}`
  );
  return data ?? { key, enabled: false, visible: false, isAdmin: false };
}

export async function getSeedBalanceOverview(): Promise<SeedBalanceOverview | null> {
  return fetchJson<SeedBalanceOverview>("/api/seeds/balance/");
}

export async function getSeedSubscription(): Promise<{
  id: string;
  packName: string;
  seedsPerMonth: number;
  priceCents: number;
  status: string;
  billingProvider: string;
  providerSubscriptionId: string;
  billingInterval: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
} | null> {
  return fetchJson("/api/seeds/subscription/");
}

export async function getDashboardSnapshot(
  limit: number = 10
): Promise<DashboardSnapshot | null> {
  return fetchJson<DashboardSnapshot>(
    `/api/dashboard/snapshot/?limit=${limit}`
  );
}

export async function getProjectsListSnapshot(
  limit: number = 10
): Promise<DashboardProject[] | null> {
  return fetchJson<DashboardProject[]>(`/api/projects/list/?limit=${limit}`);
}

export async function getConnectionsSafe(): Promise<SafeConnection[]> {
  const data = await fetchJson<{ connections: SafeConnection[] }>(
    "/api/connections/safe/"
  );
  return data?.connections ?? [];
}

export async function getConnectionGroupsSafe(): Promise<
  SafeConnectionGroup[]
> {
  return (
    (await fetchJson<SafeConnectionGroup[]>("/api/connections/groups/")) ?? []
  );
}

export async function getProjectSnapshot(
  projectId: string
): Promise<ProjectSnapshot | null> {
  return fetchJson<ProjectSnapshot>(
    `/api/projects/${encodeURIComponent(projectId)}/snapshot/`
  );
}

export async function getProjectSnapshotV2(
  projectId: string
): Promise<ProjectSnapshotV2 | null> {
  return fetchJson<ProjectSnapshotV2>(
    `/api/projects/${encodeURIComponent(projectId)}/snapshot/?v2=1`
  );
}
